import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, ChevronUp, Check } from "lucide-react";
import { toast } from "sonner";
import { api, money, errorMessage, track } from "@/lib/api";
import { BookingProvider, useBooking, STEPS } from "@/context/BookingContext";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { PriceBreakdown } from "@/components/admin/QuoteTester";
import { HomeStep, CleaningStep, ScheduleStep, ReviewStep } from "./Steps4";

const COMPONENTS = [HomeStep, CleaningStep, ScheduleStep, ReviewStep];
const EVENTS = ["booking_step1_completed", "booking_step2_completed", "booking_step3_completed"];

function Wizard() {
  const { state, patch, priceQuote, quoteError, area, reset } = useBooking();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  useSeo("Book a Cleaning", "Get an instant price and book your home cleaning online in 4 quick steps.");
  useEffect(() => { track("booking_started"); }, []);
  const step = Math.min(Math.max(state.step || 0, 0), STEPS.length - 1);
  const Step = COMPONENTS[step];
  const a = state.address;
  const postalOk = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(a.postal_code.trim());

  const canContinue = (() => {
    switch (step) {
      case 0: return postalOk && a.street.trim().length >= 3 && a.city.trim().length >= 2 && !!area?.eligible && !!priceQuote?.eligible;
      case 1: return !!priceQuote && priceQuote.eligible;
      case 2: return !!state.scheduled_date && !!state.time_slot;
      default: return true;
    }
  })();

  const next = () => { track(EVENTS[step]); patch({ step: step + 1 }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { if (step > 0) { patch({ step: step - 1 }); window.scrollTo({ top: 0, behavior: "smooth" }); } };

  const guestOk = state.as_guest && state.guest?.first_name?.trim() && state.guest?.last_name?.trim() && /^\S+@\S+\.\S+$/.test(state.guest?.email || "") && (state.guest?.phone || "").replace(/\D/g, "").length >= 7;
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bookings", { quote: { ...state.quote, hours: state.quote.hours || null, promo_code: state.quote.promo_code || null, referral_code: state.quote.referral_code || null },
        address: state.address, scheduled_date: state.scheduled_date, time_slot: state.time_slot, customer_phone: state.customer_phone || "", notes: state.notes || "", expected_total: priceQuote?.total,
        guest: !user && state.as_guest ? state.guest : null });
      track("booking_completed", { booking_number: r.data.booking_number, total: r.data.total, promo: !!state.quote.promo_code, reward: !!state.quote.reward_id, source: user ? "member" : "guest" });
      reset();
      navigate(r.data.guest_token ? `/book/confirmation/${r.data.id}?t=${r.data.guest_token}` : `/book/confirmation/${r.data.id}`, { replace: true });
      return;
    } catch (e) {
      toast.error(errorMessage(e));
      if (e?.response?.status === 409) patch({ step: 2, time_slot: "", time_display: "" });
    } finally { setBusy(false); }
  };

  const showPrice = !!priceQuote && postalOk;
  return (
    <div className="min-h-screen bg-[var(--kc-bg)]" data-testid="booking-wizard">
      <div className="sticky top-0 z-40 bg-[var(--kc-bg)]/95 backdrop-blur-md border-b border-[#E5E2DA]">
        <div className="kc-container max-w-5xl py-3">
          <div className="flex items-center justify-between text-sm">
            <button type="button" onClick={step === 0 ? () => navigate(-1) : back} className="btn-ghost h-9 px-2 -ml-2" data-testid="booking-back-button"><ArrowLeft className="w-4 h-4" /> {step === 0 ? "Exit" : "Back"}</button>
            <span className="font-semibold" data-testid="booking-progress-label">Step {step + 1} of {STEPS.length} · {STEPS[step]}</span>
          </div>
          <ol className="mt-3 grid grid-cols-4 gap-1.5" data-testid="booking-stepper">
            {STEPS.map((s, i) => <li key={s} className="flex flex-col gap-1.5"><span className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? "bg-[var(--kc-blue)]" : "bg-[#E5E2DA]"}`} /><span className={`hidden sm:flex items-center gap-1 text-[11px] font-medium ${i === step ? "text-[var(--kc-blue)]" : "text-[#5A606A]"}`}>{i < step && <Check className="w-3 h-3" />}{s}</span></li>)}
          </ol>
        </div>
      </div>

      <div className="kc-container max-w-5xl py-6 sm:py-8 pb-52 lg:grid lg:grid-cols-[1fr_320px] lg:gap-10">
        <div key={step} className="rise min-w-0"><Step /></div>
        <aside className="hidden lg:block"><div className="sticky top-28 kc-card p-6" data-testid="desktop-price-panel">
          <p className="kc-eyebrow">Estimated total</p>
          {showPrice ? <>
            <p className="mt-2 text-4xl font-display font-semibold text-[var(--kc-blue)]" data-testid="desktop-price-total">{money(priceQuote.total)}</p>
            {priceQuote.estimated_hours && <p className="text-xs text-[#5A606A]">About {priceQuote.estimated_hours} hours{priceQuote.tax?.enabled ? " · incl. tax" : ""}</p>}
            <div className="mt-4"><PriceBreakdown quote={priceQuote} compact /></div>
            {!user && priceQuote.member?.preview?.savings > 0 && <div className="mt-4 rounded-xl bg-[#EFF4FF] p-3 text-xs" data-testid="desktop-member-savings"><p className="font-semibold text-[var(--kc-blue)]">Sign in & save {money(priceQuote.member.preview.savings)}</p><p className="text-[#5A606A]">{priceQuote.member.preview.label} ({priceQuote.member.preview.percent}%) for registered customers.</p></div>}
          </> : <p className="mt-2 text-sm text-[#5A606A]">{quoteError || "Enter your postal code and home details to see your price."}</p>}
        </div></aside>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E5E2DA] shadow-[0_-8px_30px_-12px_rgba(26,29,32,0.15)]" data-testid="booking-sticky-bar">
        {showBreakdown && showPrice && <div className="kc-container max-w-5xl py-4 border-b border-[#E5E2DA] lg:hidden max-h-[45vh] overflow-y-auto" data-testid="mobile-breakdown"><PriceBreakdown quote={priceQuote} compact /></div>}
        <div className="kc-container max-w-5xl py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            {showPrice ? (
              <button type="button" onClick={() => setShowBreakdown(!showBreakdown)} className="text-left lg:pointer-events-none" data-testid="sticky-price-toggle">
                <p className="text-xs text-[#5A606A] flex items-center gap-1 whitespace-nowrap">Estimated total{priceQuote.tax?.enabled ? " incl. tax" : ""} <span className="lg:hidden inline-flex items-center">· <span className="hidden min-[400px]:inline ml-1">View </span>details <ChevronUp className={`w-3 h-3 transition-transform ${showBreakdown ? "rotate-180" : ""}`} /></span></p>
                <p className="text-2xl font-display font-semibold text-[var(--kc-blue)] leading-none" data-testid="sticky-price-total">{money(priceQuote.total)}</p>
                {!user && priceQuote.member?.preview?.savings > 0 && <p className="text-[11px] text-[#067647] font-medium mt-1" data-testid="sticky-member-savings">Members save {money(priceQuote.member.preview.savings)} on this booking</p>}
              </button>
            ) : <p className="text-sm text-[#5A606A]" data-testid="sticky-price-placeholder">{quoteError ? "Price unavailable — please retry" : "Your estimate appears here"}</p>}
          </div>
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={next} disabled={!canContinue} className="btn-primary h-12 px-6 sm:px-8" data-testid="booking-continue-button">Continue <ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button type="button" onClick={confirm} disabled={busy || !priceQuote || (!user && !guestOk)} className="btn-primary h-12 px-5 sm:px-8 text-sm" data-testid="booking-confirm-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><span className="sm:hidden">CONFIRM</span><span className="hidden sm:inline">CONFIRM BOOKING</span></>}</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BookingWizard() {
  return <BookingProvider><Wizard /></BookingProvider>;
}
